/**
 * @file Anything that can run on every page.
 */

import debounce from 'just-debounce-it';
import { floatingLabels } from '../js/helpers/floating-labels';

/**
 * Global functions.
 *
 * Runs component js and any global tasks.
 */
const vGlobal = () => {
	import('../components/main-nav/main-nav').then(({ default: vMainNav }) => {
		vMainNav();
	});

	import('../components/menu/menu').then(({ default: vMenu }) => {
		vMenu();
	});

	if (document.querySelector('.v-hp-hero')) {
		import('../components/hp-hero/hp-hero').then(({ default: vHpHero }) => {
			vHpHero();
		});
	}

	if (document.querySelector('.v-scrolling-stats')) {
		import('../components/scrolling-stats/scrolling-stats').then(
			({ default: vScrollingStats }) => {
				vScrollingStats();
			},
		);
	}

	if (document.querySelector('.v-hp-injuries')) {
		import('../components/hp-injuries/hp-injuries').then(
			({ default: vHpInjuries }) => {
				vHpInjuries();
			},
		);
	}

	import('../components/button-load-more/button-load-more').then(
		({ default: vButtonLoadMore }) => {
			vButtonLoadMore();
		},
	);

	/**
	 * Get scrollbar width.
	 * @returns {number} Scrollbar width.
	 */
	function getScrollbarWidth() {
		const outer = document.createElement('div');
		outer.style.visibility = 'hidden';
		outer.style.overflow = 'scroll';
		document.body.appendChild(outer);

		const inner = document.createElement('div');
		outer.appendChild(inner);

		const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

		outer.parentNode.removeChild(outer);

		return scrollbarWidth;
	}

	document.documentElement.style.setProperty(
		'--scrollbar-width',
		`${getScrollbarWidth()}px`,
	);
	window.addEventListener(
		'resize',
		debounce(() => {
			document.documentElement.style.setProperty(
				'--scrollbar-width',
				`${getScrollbarWidth()}px`,
			);
		}),
		100,
	);

	const selects = document.querySelectorAll('select');
	[...selects].forEach((select) => {
		const selectParent = select.parentNode;
		if (selectParent.classList.contains('v-form__select')) {
			return;
		}

		const wrapper = document.createElement('span');
		const arrow = document.createElement('span');
		wrapper.classList.add('v-form__select');
		arrow.classList.add('v-form__select-arrow');
		selectParent.insertBefore(wrapper, select);
		wrapper.appendChild(select);
		wrapper.appendChild(arrow);
		select.classList.add('v-form__select-field');
	});

	const tableWrappers = document.querySelectorAll('.v-table-wrapper');
	/**
	 * Toggle scroll indicator classes on table wrappers.
	 * @param {Element} tableWrapper - Table wrapper.
	 * @param {Element} tableInner - Scrollable inner table wrapper.
	 */
	const tableToggleClasses = (tableWrapper, tableInner) => {
		if (tableInner.scrollLeft > 2) {
			tableWrapper.classList.add('v-table-wrapper--scrollable-start');
		} else {
			tableWrapper.classList.remove('v-table-wrapper--scrollable-start');
		}

		if (
			tableInner.scrollLeft + tableInner.clientWidth <
			tableInner.scrollWidth - 2
		) {
			tableWrapper.classList.add('v-table-wrapper--scrollable-end');
		} else {
			tableWrapper.classList.remove('v-table-wrapper--scrollable-end');
		}
	};

	tableWrappers.forEach((tableWrapper) => {
		const inner = tableWrapper.querySelector('.v-table-wrapper__inner');
		inner.addEventListener(
			'scroll',
			tableToggleClasses.bind(null, tableWrapper, inner),
			{ passive: true },
		);

		const observer = new ResizeObserver(() => {
			tableToggleClasses(tableWrapper, inner);
		});
		observer.observe(tableWrapper, { box: 'border-box' });
		tableToggleClasses(tableWrapper, inner);
	});

	// Initialize floating labels
	floatingLabels();

	/**
	 * Global Window Load.
	 *
	 * Runs polyfills on page load.
	 */
	async function onWindowLoad() {
		const body = document.querySelector('body');
		if (!body.classList.contains('admin-bar')) {
			const { listen } = await import('quicklink');

			listen({
				ignores: [
					(url) => {
						const cleanUrl = url.replace(/#.*/, '');
						return cleanUrl === window.location.href;
					},
					/.*\/wp-admin\/.*/,
				],
			});
		}
	}

	if (document.readyState === 'complete') {
		onWindowLoad();
	} else {
		window.addEventListener('load', onWindowLoad);
	}
};

if (document.readyState !== 'loading') {
	vGlobal();
} else {
	document.addEventListener('DOMContentLoaded', vGlobal);
}
